import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '@/global/components/ui/tabs'
import { cn } from '@/lib/utils'
import { createContext, useState } from 'react'
import Dialog from '.'
import { Actions } from './DialogGuideActions'
import { DialogContentProps } from './DialogGuideContent'
import { DialogRootProps } from './DialogGuideRoot'
import { DialogTitleProps } from './DialogGuideTitle'

interface ITab {
    name: string
    label: string
    content: React.ReactNode
}

interface TabsContextProps {
    tabs: ITab[]
    defaultTab?: string
    onTabChange?: (tab: string) => any
}

type TabbedDialogProps = DialogRootProps & TabsContextProps

const TabbedDialogContext = createContext<{
    tabs: ITab[]
}>(null!)

function TabbedRoot({
    tabs,
    defaultTab,
    title,
    onTabChange,
    children,
    width = '3xl',
    className,
    ...props
}: TabbedDialogProps) {
    const [tab, setTab] = useState(defaultTab || tabs[0].name)

    const onChange = (tab: string) => {
        setTab(tab)
        onTabChange?.(tab)
    }

    return (
        <TabbedDialogContext.Provider value={{ tabs }}>
            <Tabs value={tab} onValueChange={onChange} className="contents">
                <Dialog.Root
                    {...props}
                    width={width}
                    // Altura cheia: com abas, o diálogo não pode mudar de
                    // tamanho a cada troca de aba.
                    className={cn('sm:h-full', className)}
                >
                    {children}
                </Dialog.Root>
            </Tabs>
        </TabbedDialogContext.Provider>
    )
}

function TabbedHeader({ children, ...props }: DialogTitleProps) {
    return (
        <TabbedDialogContext.Consumer>
            {({ tabs }) => (
                <Dialog.Title
                    {...props}
                    className={cn('pb-0 min-h-fit', props.className)}
                >
                    {children}
                    <TabsList variant="underline" className="mb-0">
                        {tabs.map(({ name, label }) => (
                            <TabsTrigger
                                key={name}
                                variant="underline"
                                value={name}
                            >
                                {label}
                            </TabsTrigger>
                        ))}
                    </TabsList>
                </Dialog.Title>
            )}
        </TabbedDialogContext.Consumer>
    )
}

function TabbedContent({ children, ...props }: DialogContentProps) {
    return (
        <TabbedDialogContext.Consumer>
            {({ tabs }) =>
                tabs.map(({ name, content }) => (
                    <TabsContent
                        key={name}
                        value={name}
                        // `data-[state=active]:flex`, e não `flex`: a aba
                        // inativa continua montada com `hidden`, e um `flex`
                        // solto venceria o `display:none` do atributo — as
                        // outras abas ficariam ocupando a altura, vazias.
                        className="min-h-0 flex-1 flex-col p-0 data-[state=active]:flex"
                    >
                        <Dialog.Content {...props}>
                            {children}
                            {content}
                        </Dialog.Content>
                    </TabsContent>
                ))
            }
        </TabbedDialogContext.Consumer>
    )
}

/** Mesma marca do `Dialog.Content` — o `Root` bloqueia tecla de fechar. */
TabbedContent.isConteudoDoDialogo = true

/**
 * @example
 * import TabbedDialog from 'global/guides/Dialog/TabbedDialog'
 * 
    <TabbedDialog.Root
        tabs={[
            {
                name: '1',
                label: 'One',
                content: <FirstTab />,
            },
            {
                name: '1',
                label: 'Two',
                content: <SecondTab />,
            },
        ]}
        onSubmit={handleSubmit}
        loading={isLoading}
        schema={schema}
    >
        <TabbedDialog.Title>Dialog title</TabbedDialog.Title>
        <TabbedDialog.Content />
        <TabbedDialog.Actions>
            <TabbedDialog.Actions.SecondaryButton />
            <TabbedDialog.Actions.PrimaryButton closeOnSubmit />
        </TabbedDialog.Actions>
    </TabbedDialog.Root>
 */
export default {
    Root: TabbedRoot,
    Title: TabbedHeader,
    Content: TabbedContent,
    Actions,
}
